import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";

export function WebGLError() {
  const { t } = useTranslation();

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        padding: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "background.default",
        backgroundImage: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: 640,
          width: "100%",
          my: "auto",
          py: { xs: 2, sm: 3, md: 4 },
          animation: "fadeIn 0.5s ease-in",
          "@keyframes fadeIn": {
            from: {
              opacity: 0,
              transform: "translateY(-20px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
      >
        <Card
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "background.paper",
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
            {/* Error Icon and Title */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 3 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  backgroundColor: "error.light",
                  color: "error.main",
                  flexShrink: 0,
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 40 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 0.5,
                  }}
                >
                  {t("webglError.title")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  {t("webglError.subtitle")}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 3 }} />

            {/* Error Message */}
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                mb: 3,
                lineHeight: 1.7,
              }}
            >
              {t("webglError.message")}
            </Typography>

            {/* Instructions */}
            <Box
              sx={{
                p: 2.5,
                backgroundColor: "background.paper",
                borderRadius: 2,
                mb: 4,
                border: "1px solid",
                borderColor: "info.main",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  mb: 1.5,
                }}
              >
                {t("webglError.howToEnable")}
              </Typography>
              <Typography
                variant="body2"
                component="ul"
                sx={{
                  color: "text.primary",
                  pl: 2.5,
                  mb: 0,
                  "& li": {
                    mb: 1,
                    "&:last-child": {
                      mb: 0,
                    },
                  },
                }}
              >
                <li>{t("webglError.step1")}</li>
                <li>{t("webglError.step2")}</li>
                <li>{t("webglError.step3")}</li>
              </Typography>
            </Box>

            {/* Action Button */}
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                size="large"
                onClick={handleReload}
                startIcon={<RefreshIcon />}
                sx={{
                  flex: 1,
                  py: 1.5,
                  px: 4,
                  fontSize: "1rem",
                }}
              >
                {t("webglError.reload")}
              </Button>
            </Stack>

            {/* Help Text */}
            <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                {t("webglError.helpText")}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
